import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Headphones } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { supabase } from '../../../lib/supabase/client';
import type { AudioQtCoverVariant, AudioQtItem } from '../data/audioQts';
import { AUDIO_QTS, AUDIO_SIGNED_URL_TTL_SEC, AUDIO_STORAGE_BUCKET } from '../data/audioQts';

const coverGradient: Record<AudioQtCoverVariant, string> = {
  lilacDawn:
    'bg-gradient-to-br from-brandLilac via-[#9b9de8] to-brandDeep opacity-[0.92]',
  tealMist:
    'bg-gradient-to-br from-brandTeal via-[#2a7a85] to-brandSage opacity-[0.92]',
  coralGlow:
    'bg-gradient-to-br from-brandCoral via-brandGold to-brandLilac opacity-[0.88]'
};

const coverOrb: Record<AudioQtCoverVariant, string> = {
  lilacDawn: 'bg-brandGold/35 blur-3xl',
  tealMist: 'bg-backgroundElevated/40 blur-3xl',
  coralGlow: 'bg-brandDeep/25 blur-3xl'
};

type AudioLoadState = 'loading' | 'ready' | 'error';

function AudioQtCard({
  item,
  audioSrc,
  audioState,
  onRequestPlay
}: {
  item: AudioQtItem;
  audioSrc: string | null;
  audioState: AudioLoadState;
  onRequestPlay: () => void;
}) {
  const hasCover = Boolean(item.coverUrl);

  return (
    <article
      className="flex flex-col overflow-hidden rounded-3xl border border-borderSoft bg-surface shadow-soft-elevated"
      aria-labelledby={`${item.id}-title`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-surfaceMuted">
        {hasCover ? (
          <>
            <img
              src={item.coverUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/10"
              aria-hidden
            />
            <div className="pointer-events-none absolute bottom-3 left-3 flex h-11 w-11 items-center justify-center rounded-full bg-surfaceGlass/95 text-brandDeep shadow-soft-elevated backdrop-blur-sm">
              <Headphones className="h-5 w-5 text-primary" aria-hidden strokeWidth={2} />
            </div>
          </>
        ) : (
          <>
            <div className={`absolute inset-0 ${coverGradient[item.coverVariant]}`} aria-hidden />
            <div
              className={`pointer-events-none absolute -right-8 -top-12 h-40 w-40 rounded-full ${coverOrb[item.coverVariant]}`}
              aria-hidden
            />
            <div
              className={`pointer-events-none absolute -bottom-16 -left-10 h-44 w-44 rounded-full ${coverOrb[item.coverVariant]}`}
              aria-hidden
            />
            <div
              className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_55%)]"
              aria-hidden
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surfaceGlass/90 text-brandDeep shadow-soft-elevated backdrop-blur-md">
                <Headphones className="h-7 w-7 text-primary" aria-hidden strokeWidth={1.75} />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 px-4 pb-4 pt-3">
        <h3 id={`${item.id}-title`} className="font-display text-lg leading-snug text-text">
          {item.title}
        </h3>
        <p className="text-xs font-bold leading-relaxed text-textMuted">{item.description}</p>
        {audioState === 'loading' && (
          <p className="mt-1 text-xs text-textMuted" aria-live="polite">
            Preparing audio…
          </p>
        )}
        {audioState === 'error' && (
          <p className="mt-1 text-xs text-danger" role="alert">
            Could not load this track. Check the file name in Supabase Storage matches the app list, and that anon users may
            read the audio bucket (SQL migration adds this policy).
          </p>
        )}
        {audioState === 'ready' && audioSrc ? (
          <Button type="button" variant="primary" size="md" fullWidth className="mt-1" onClick={onRequestPlay}>
            Play
          </Button>
        ) : null}
      </div>
    </article>
  );
}

export const ListenSection: React.FC<{ prioritizedIds?: string[]; onlyIds?: string[] }> = ({
  prioritizedIds = [],
  onlyIds
}) => {
  const playingIdRef = useRef<string | null>(null);
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const modalAudioRef = useRef<HTMLAudioElement | null>(null);

  const [audioModalId, setAudioModalId] = useState<string | null>(null);
  const [audioSrcById, setAudioSrcById] = useState<Record<string, string>>({});
  const [audioStateById, setAudioStateById] = useState<Record<string, AudioLoadState>>(() => {
    const initial: Record<string, AudioLoadState> = {};
    AUDIO_QTS.forEach((t) => {
      initial[t.id] = 'loading';
    });
    return initial;
  });
  const [signBatchError, setSignBatchError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

    if (!url || !anon) {
      setSignBatchError('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY — cannot request audio URLs.');
      setAudioStateById((prev) => {
        const next = { ...prev };
        AUDIO_QTS.forEach((t) => {
          next[t.id] = 'error';
        });
        return next;
      });
      return;
    }

    (async () => {
      const results = await Promise.all(
        AUDIO_QTS.map(async (item) => {
          const { data, error } = await supabase.storage
            .from(AUDIO_STORAGE_BUCKET)
            .createSignedUrl(item.storagePath, AUDIO_SIGNED_URL_TTL_SEC);

          if (error) {
            console.error('[Listen] createSignedUrl failed', item.storagePath, error.message);
            return { id: item.id, signedUrl: null as string | null, error: error.message };
          }
          return { id: item.id, signedUrl: data?.signedUrl ?? null, error: null as string | null };
        })
      );

      if (cancelled) return;

      let anyOk = false;
      let firstErr: string | null = null;

      const srcUpdates: Record<string, string> = {};
      const stateUpdates: Record<string, AudioLoadState> = {};

      AUDIO_QTS.forEach((item, idx) => {
        const r = results[idx];
        if (r.signedUrl) {
          srcUpdates[item.id] = r.signedUrl;
          stateUpdates[item.id] = 'ready';
          anyOk = true;
        } else {
          stateUpdates[item.id] = 'error';
          if (!firstErr && r.error) firstErr = r.error;
        }
      });

      setAudioSrcById(() => srcUpdates);
      setAudioStateById((prev) => ({ ...prev, ...stateUpdates }));
      if (!anyOk && firstErr && AUDIO_QTS.length > 0) {
        setSignBatchError(firstErr);
      } else if (AUDIO_QTS.length > 0) {
        setSignBatchError(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const registerAudio = useCallback((id: string, el: HTMLAudioElement | null) => {
    if (!el) {
      audioElsRef.current.delete(id);
      return;
    }
    audioElsRef.current.set(id, el);
  }, []);

  const onPlayStart = useCallback((id: string) => {
    if (playingIdRef.current && playingIdRef.current !== id) {
      const prev = audioElsRef.current.get(playingIdRef.current);
      prev?.pause();
    }
    playingIdRef.current = id;
  }, []);

  const closeAudioModal = useCallback(() => {
    modalAudioRef.current?.pause();
    setAudioModalId(null);
  }, []);

  useEffect(() => {
    if (!audioModalId) return;
    const id = requestAnimationFrame(() => {
      modalAudioRef.current?.play().catch(() => {});
    });
    return () => cancelAnimationFrame(id);
  }, [audioModalId]);

  const modalItem = audioModalId ? AUDIO_QTS.find((t) => t.id === audioModalId) : undefined;
  const prioritizedSet = new Set(prioritizedIds);
  const onlySet = onlyIds ? new Set(onlyIds) : null;
  const orderedAudioQts = [...AUDIO_QTS].sort((a, b) => {
    const aPriority = prioritizedSet.has(a.id) ? 0 : 1;
    const bPriority = prioritizedSet.has(b.id) ? 0 : 1;
    return aPriority - bPriority;
  });
  const visibleAudioQts = onlySet ? orderedAudioQts.filter((item) => onlySet.has(item.id)) : orderedAudioQts;

  return (
    <section className="mt-8" aria-labelledby="explore-listen-heading">
      <div className="mb-4 flex items-start gap-3">
        <div>
          {signBatchError ? (
            <p className="mt-2 rounded-xl border border-borderSoft bg-surfaceElevated px-3 py-2 text-xs text-danger" role="alert">
              {signBatchError}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
        {visibleAudioQts.map((item) => (
          <div key={item.id} id={item.id} className="scroll-mt-24">
            <AudioQtCard
              item={item}
              audioSrc={audioSrcById[item.id] ?? null}
              audioState={audioStateById[item.id] ?? 'loading'}
              onRequestPlay={() => setAudioModalId(item.id)}
            />
          </div>
        ))}
      </div>

      <Modal open={audioModalId !== null} onClose={closeAudioModal} title={modalItem?.title}>
        {audioModalId && audioSrcById[audioModalId] ? (
          <div className="space-y-4">
            {modalItem?.coverUrl ? (
              <div className="overflow-hidden rounded-2xl border border-borderSoft/40 bg-surfaceMuted">
                <img
                  src={modalItem.coverUrl}
                  alt={`${modalItem.title} placeholder`}
                  className="aspect-[4/3] w-full object-cover"
                  loading="eager"
                  decoding="async"
                />
              </div>
            ) : null}
            <audio
              key={audioModalId}
              ref={(el) => {
                modalAudioRef.current = el;
                registerAudio(audioModalId, el);
              }}
              controls
              preload="metadata"
              className="h-11 w-full accent-primary"
              onPlay={() => onPlayStart(audioModalId)}
            >
              <source src={audioSrcById[audioModalId]} type="audio/mpeg" />
              Your browser does not support embedded audio.
            </audio>
          </div>
        ) : null}
      </Modal>
    </section>
  );
};
